import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';
import { Option, Select } from 'carbon-react/lib/components/select';

import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';
import { WizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WorkspaceMember } from '@futrli-sdk/people';

import { EmailFields } from '../../../../ui/email-fields/email-fields';
import { EmailPreview } from '../../../../report-builder/components/email-preview/email-preview';
import { Recurrence, ScheduleReportFormState } from '../hooks/useScheduleReportForm';
import { Recurrence as RecurrenceSettingsRecurrence } from '../../../../ui/recurrence-settings/types';
import { RecurrenceSelect } from '../components/recurrence-select';
import { ReviewBeforeSending } from '../components/review-before-sending';
import { ScheduleRecurrenceSettings } from '../../../../ui/recurrence-settings/schedule-recurrence-settings';

interface ScheduleFormScreenProps {
  reportName: string;
  members: WorkspaceMember[];
  form: ScheduleReportFormState;
  isEditing: boolean;
  error?: string;
  onCancel: () => void;
  onSubmit: () => void;
}

const RECURRENCE_TO_SETTINGS: Partial<Record<Recurrence, RecurrenceSettingsRecurrence>> = {
  [Recurrence.DAILY]: RecurrenceSettingsRecurrence.DAILY,
  [Recurrence.WEEKLY]: RecurrenceSettingsRecurrence.WEEKLY,
  [Recurrence.MONTHLY]: RecurrenceSettingsRecurrence.MONTHLY
};

export const ScheduleFormScreen = ({
  reportName,
  members,
  form,
  isEditing,
  error,
  onCancel,
  onSubmit
}: ScheduleFormScreenProps) => {
  const intl = useIntl();
  const [isOpenPreviewEmail, setIsOpenPreviewEmail] = useState(false);

  const handleRecurrenceSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    form.setRecurrence(event.target.value as Recurrence);
  };

  // Map the schedule recurrence to the RecurrenceSettings component's expected enum
  // RecurrenceSettings supports DAILY, WEEKLY, MONTHLY. For QUARTERLY/ANNUALLY we show MONTHLY settings.
  const recurrenceForSettings = useMemo(() => {
    if (form.recurrence === Recurrence.QUARTERLY || form.recurrence === Recurrence.ANNUALLY) {
      return RecurrenceSettingsRecurrence.MONTHLY;
    }
    return RECURRENCE_TO_SETTINGS[form.recurrence] || RecurrenceSettingsRecurrence.DAILY;
  }, [form.recurrence]);

  const rightSideButtons = (
    <>
      <Button buttonType="tertiary" onClick={() => setIsOpenPreviewEmail(true)}>
        {intl.formatMessage({ id: 'reporting.preview_email', defaultMessage: 'Preview email' })}
      </Button>
      <Button buttonType="primary" onClick={onSubmit}>
        {isEditing
          ? intl.formatMessage({ id: 'common.update', defaultMessage: 'Update' })
          : intl.formatMessage({ id: 'common.create', defaultMessage: 'Create' })}
      </Button>
    </>
  );

  return (
    <>
      <WizardScreen rightSideButtons={rightSideButtons} onCancel={onCancel}>
        <>
          {error && <WizardFormError message={error} mb={3} />}

          <WizardFormValidationErrorSummary errors={form.validationErrors} mb={3} />

          <WizardRequiredFieldInfo />

          <Select
            id="schedule-report-name"
            name="schedule-report-name"
            label={intl.formatMessage({ id: 'reporting.report', defaultMessage: 'Report' })}
            value={reportName}
            onChange={() => {}}
            disabled
            required
            mb="16px"
          >
            <Option text={reportName} value={reportName} />
          </Select>

          <Box mb="16px">
            <DateInput
              inputHint="DD/MM/YYYY"
              label={intl.formatMessage({ id: 'predict.send_date', defaultMessage: 'Send date' })}
              required
              mb="0"
              width="180px"
              value={intl.formatDate(form.recurrenceStartDate)}
              onChange={form.handleStartDateChange as (e: DateChangeEvent) => void}
              error={
                form.recurrenceStartDateError
                  ? intl.formatMessage({
                      id: 'reporting.reset_start_date_to_today',
                      defaultMessage: 'Reset the start date to today or a future date'
                    })
                  : ''
              }
            />
          </Box>

          <Box mt={2}>
            <RecurrenceSelect value={form.recurrence} onChange={handleRecurrenceSelectChange} />
          </Box>

          {form.showRecurrenceSettings && (
            <Box mt={2}>
              <ScheduleRecurrenceSettings
                recurrence={recurrenceForSettings}
                recurrenceStartDate={form.recurrenceStartDate}
                recurrenceRepeatValue={form.recurrenceRepeatValue}
                recurrenceRepeatValueId={form.FIELD_IDS.EVERY}
                recurrenceRepeatValueDisabled={
                  form.recurrence === Recurrence.QUARTERLY || form.recurrence === Recurrence.ANNUALLY
                }
                recurrenceRepeatValueError={form.validationErrors[form.FIELD_IDS.EVERY]?.message}
                recurrenceEndType={form.recurrenceEndType}
                occurancesValue={form.occurancesValue}
                recurrenceEndDate={form.recurrenceEndDate}
                recurrenceEndDateIsBeforeStartDate={form.recurrenceEndDateIsBeforeStartDate}
                recurrenceMonthDay={form.recurrenceMonthDay}
                recurrenceWeekDay={form.recurrenceWeekDay}
                onRepeatValueChange={form.handleRepeatValueChange}
                onRepeatValueBlur={form.revalidate}
                onEndTypeChange={form.handleEndTypeChange}
                onOccurancesValueChange={form.handleOccurancesValueChange}
                onEndDateChange={form.handleEndDateChange as (e: DateChangeEvent) => void}
                onWeekDayChange={form.handleWeekDayChange}
                onMonthDayChange={form.handleMonthDayChange}
              />
            </Box>
          )}

          <Box mt={3} mb={3} backgroundColor="#CCD6DB" height="1px" />

          {form.recurrence !== Recurrence.DOES_NOT_REPEAT && (
            <Box mb={3}>
              <ReviewBeforeSending value={form.reviewBeforeSending} onChange={form.setReviewBeforeSending} />
            </Box>
          )}

          <EmailFields
            members={members}
            selectedUsers={form.selectedUsers}
            setSelectedUsers={form.setSelectedUsers}
            emailSubject={form.emailSubject}
            setEmailSubject={form.setEmailSubject}
            emailBody={form.emailBody}
            setEmailBody={form.setEmailBody}
            validationErrors={form.validationErrors}
            revalidate={form.revalidate}
            fieldIds={form.FIELD_IDS}
            placeholder={intl.formatMessage({ id: 'predict.select_recipients', defaultMessage: 'Select recipients' })}
            inputHint={intl.formatMessage({
              id: 'predict.email_pdf_send_to_hint',
              defaultMessage: 'Select one or more. Type to search if needed.'
            })}
          />
        </>
      </WizardScreen>

      <EmailPreview
        isOpen={isOpenPreviewEmail}
        handleClose={() => setIsOpenPreviewEmail(false)}
        emailSubject={form.emailSubject}
        emailBody={form.emailBody}
      />
    </>
  );
};
